import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import EditPencilIcon from "@redotech/redo-icons/edit-05.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { TrackingPageThumbnail } from "@redotech/redo-merchant-marketing-app/order-tracking/tracking-page/components/tracking-page-thumbnail";
import { TrackingPageStep } from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Text } from "@redotech/redo-web/text";
import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FrontendStepType, InvalidFlowStep, PartialWithRequiredType } from "..";
import { InvalidStepsList } from "../../components/invalid-steps-list";
import { useDelayedAutoFocus } from "../common/use-delayed-auto-focus";

interface Props {
  step: PartialWithRequiredType<TrackingPageStep>;
  setStep: (step: PartialWithRequiredType<TrackingPageStep>) => void;
  invalidSteps?: InvalidFlowStep[];
  setSelectedId?: (id: string) => void;
  showValidationErrors?: boolean;
  save?: () => void;
}

function TrackingPagePreview({
  step,
  setStep,
  invalidSteps,
  setSelectedId,
  showValidationErrors,
  save,
}: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const marketingRpcClient = useRequiredContext(RedoMarketingRpcClientContext);

  const otherInvalidSteps = invalidSteps?.filter((s) => s.id !== step.id) || [];
  const hasOtherInvalidSteps = !!otherInvalidSteps.length;
  const delayedAutoFocusInvalidSteps =
    useDelayedAutoFocus(hasOtherInvalidSteps);

  const trackingPagesLoad = useLoad(async () => {
    const trackingPages = await marketingRpcClient.getTrackingPages({});
    return trackingPages.map((page) => ({
      _id: page._id,
      name: page.name || "Untitled",
    }));
  }, [marketingRpcClient]);

  const trackingPageOptions = useMemo<TextRedoListItem<string>[]>(() => {
    if (!trackingPagesLoad.value) {
      return [];
    }

    return trackingPagesLoad.value.map((page) => ({
      id: page._id,
      value: page._id,
      type: "text",
      text: page.name,
    }));
  }, [trackingPagesLoad.value]);

  const selectedOption = useMemo(
    () =>
      trackingPageOptions.find(
        (option) => option.value === step.trackingPageId,
      ),
    [trackingPageOptions, step.trackingPageId],
  );

  const selectedPageLoad = useLoad(async () => {
    if (!step.trackingPageId) {
      return undefined;
    }
    const page = await marketingRpcClient.getTrackingPageById({
      id: step.trackingPageId,
    });
    return page?.settings;
  }, [marketingRpcClient, step.trackingPageId]);

  const templateState = useMemo(() => {
    if (step.trackingPageId && trackingPagesLoad.value) {
      const page = trackingPagesLoad.value.find(
        (p) => p._id === step.trackingPageId,
      );
      if (page) {
        return { state: "exists" as const, page };
      }
      return { state: "deleted" as const };
    }
    return { state: "create" as const };
  }, [step.trackingPageId, trackingPagesLoad.value]);

  if (trackingPagesLoad.pending) {
    return (
      <Flex align="center" grow={1} justify="center">
        <LoadingRedoAnimation />
      </Flex>
    );
  }

  const isReadOnly = templateState.state === "exists";
  const page =
    templateState.state === "exists" ? templateState.page : undefined;

  return (
    <Flex dir="column" gap="2xl" justify="center">
      <Flex dir="column" gap="xxs">
        <Text fontSize="xs" textColor="secondary">
          Tracking page
        </Text>
        <RedoSingleSelectDropdownInput<string>
          disabled={hasOtherInvalidSteps}
          options={trackingPageOptions}
          optionSelected={({ item }) => {
            const selectedPage = trackingPagesLoad.value?.find(
              (p) => p._id === item.value,
            );
            if (!selectedPage) {
              return;
            }

            setStep({ ...step, trackingPageId: selectedPage._id });
          }}
          placeholder="Select a tracking page"
          selectedItem={selectedOption}
          size="sm"
        />
      </Flex>

      <Flex dir="column" gap="xxs">
        <Flex align="center" dir="row" justify="space-between" mb="md">
          <Text fontSize="xs" textColor="secondary">
            Content
          </Text>
          {isReadOnly && (
            <RedoButton
              disabled={hasOtherInvalidSteps}
              hierarchy="secondary"
              IconLeading={EditPencilIcon}
              onClick={async () => {
                if (save) {
                  await save();
                }
                const returnPath = `returnPath=${encodeURIComponent(location.pathname)}&selected=${step.id}`;
                navigate(
                  `../../tracking-page/tracking-page-builder?id=${step.trackingPageId}&${returnPath}`,
                );
              }}
              size="xs"
              text="Edit"
            />
          )}
        </Flex>
        <Flex
          bgColor="primary"
          borderColor={
            !isReadOnly && !page && showValidationErrors ? "error" : "primary"
          }
          borderStyle="solid"
          borderWidth="1px"
          dir="column"
          p="sm"
          position="relative"
          radius="sm"
          style={{ minHeight: "200px" }}
        >
          {hasOtherInvalidSteps && setSelectedId ? (
            <InvalidStepsList
              autoFocus={delayedAutoFocusInvalidSteps}
              invalidSteps={otherInvalidSteps}
              key={delayedAutoFocusInvalidSteps.toString()}
              onGoToStep={setSelectedId}
            />
          ) : page ? (
            <Flex dir="column" height="full" overflow="hidden" radius="sm">
              <TrackingPageThumbnail
                settings={selectedPageLoad.value ?? undefined}
              />
            </Flex>
          ) : (
            <Flex
              align="center"
              bgColor="primary"
              height="full"
              justify="center"
              style={{ minHeight: "200px" }}
            >
              <RedoButton
                disabled={hasOtherInvalidSteps}
                hierarchy="secondary"
                IconLeading={PlusIcon}
                onClick={async () => {
                  try {
                    const newPage = await marketingRpcClient.createTrackingPage(
                      { name: "New Tracking Page" },
                    );

                    setStep({ ...step, trackingPageId: newPage._id });

                    if (save) {
                      await save();
                    }

                    const returnPath = `returnPath=${encodeURIComponent(location.pathname)}&selected=${step.id}`;
                    navigate(
                      `../../tracking-page/tracking-page-builder?id=${newPage._id}&${returnPath}`,
                    );
                  } catch (error) {
                    console.error("Failed to create tracking page:", error);
                  }
                }}
                text="Create tracking page"
                theme="action"
              />
            </Flex>
          )}
        </Flex>
        {!isReadOnly && !page && showValidationErrors && (
          <Text fontSize="xs" textColor="error">
            {hasOtherInvalidSteps
              ? `Fix invalid ${otherInvalidSteps.length > 1 ? "steps" : "step"} before selecting a page`
              : "Tracking page is required"}
          </Text>
        )}
      </Flex>
    </Flex>
  );
}

export const trackingPageStepType: FrontendStepType<
  PartialWithRequiredType<TrackingPageStep>
> = {
  type: "standard",
  valid: (step) => !!step.trackingPageId,
  selectable: (_) => true,
  inspectorFactory: (props) => ({
    header: { title: "Tracking page" },
    sections: [
      {
        fields: [
          {
            widget: "custom",
            Element: ({ value: step }) => (
              <TrackingPagePreview
                invalidSteps={props.invalidSteps}
                save={props.save}
                setSelectedId={props.setSelectedId}
                setStep={props.setStep}
                showValidationErrors={props.showValidationErrors}
                step={step}
              />
            ),
          },
        ],
      },
    ],
  }),
};
